import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, UserRound } from "lucide-react";
import { Link, NavLink, isFrPath, stripFr } from "@/components/LocalLink";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/context/SiteContext";

const NAV = [["Shop", "/shop", "shop"], ["Find your jar", "/find-your-jar", "find-your-jar"], ["Our Story", "/our-story", "our-story"], ["Help", "/help", "help"]];
export const WORDMARK = "/assets/brand/dp-wordmark.webp";

export const TopStrip = () => {
  const { orderingOpen, notify } = useSite();
  if (orderingOpen) return <div className="strip" data-testid="top-strip">Free shipping on orders over $125 · <b>Ships across Canada</b></div>;
  return (
    <button type="button" className="strip" style={{ width: "100%", display: "block" }} data-testid="top-strip" onClick={() => notify()}>
      Ordering opens soon. <b>Join the list for free shipping on your first order →</b>
    </button>
  );
};

export const LangSwitch = ({ testid = "lang", cls = "lang" }) => {
  const { lang, setLang } = useLang();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const go = (l) => {
    setLang(l);
    const base = stripFr(pathname);
    navigate(`${l === "fr" ? `/fr${base === "/" ? "" : base}` : base}${search}${hash}`);
  };
  return (
    <span className={cls} data-no-translate>
      {["en", "fr"].map((l, i) => (
        <span key={l}>{i ? " / " : ""}
          <button type="button" data-testid={`${testid}-${l}`} onClick={() => go(l)} aria-pressed={lang === l}
            style={{ color: lang === l ? "var(--brass)" : "var(--muted)", letterSpacing: ".14em" }}>{l.toUpperCase()}</button>
        </span>
      ))}
    </span>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const fr = isFrPath(useLocation().pathname);
  return (
    <>
      <TopStrip />
      <header className="nav" data-testid="site-header" style={{ position: "sticky", top: 0, zIndex: 50 }}>
        <div className="wrap">
          <Link to="/" data-testid="brand-home-link"><img src={WORDMARK} alt="Dirty Paws Apothecary" /></Link>
          <nav data-testid="primary-nav" aria-label="Main">
            {NAV.map(([label, href, id]) => (
              <NavLink key={id} to={href} data-testid={`nav-${id}`} style={({ isActive }) => (isActive ? { color: "var(--brass)" } : undefined)}>{label}</NavLink>
            ))}
          </nav>
          <div className="right">
            <LangSwitch />
            <a href={fr ? "/fr/account" : "/account"} className="acct" aria-label="Your account" data-testid="header-account-button"><UserRound className="icon" /></a>
            <button type="button" className="cart" onClick={() => setOpen(true)} aria-label="Open cart" data-testid="header-cart-button">
              <ShoppingBag className="icon" />
              {count > 0 && <span data-testid="cart-count-badge" style={{ position: "absolute", top: -6, right: -8, background: "var(--moss)", color: "var(--cream)", fontSize: 10, borderRadius: 9, minWidth: 16, height: 16, lineHeight: "16px", textAlign: "center" }}>{count}</span>}
            </button>
            <button type="button" className="burger" onClick={() => setMenu((m) => !m)} aria-label="Menu" aria-expanded={menu} data-testid="mobile-menu-button">
              {menu ? <X className="icon" /> : <Menu className="icon" />}
            </button>
          </div>
        </div>
        {menu && (
          <nav data-testid="mobile-nav" aria-label="Main" style={{ display: "flex", flexDirection: "column", gap: 16, padding: "18px 20px 22px", borderTop: "1px solid var(--line)", background: "var(--bg)" }}>
            {NAV.map(([label, href, id]) => (
              <Link key={id} to={href} onClick={() => setMenu(false)} data-testid={`mobile-nav-${id}`} className="serif" style={{ fontSize: 26, color: "var(--cream)" }}>{label}</Link>
            ))}
            <a href={fr ? "/fr/account" : "/account"} data-testid="mobile-nav-account" style={{ fontSize: 14, color: "var(--text)" }}>Your account</a>
            <div className="mobile-lang" style={{ fontSize: 13 }}><LangSwitch testid="mobile-lang" cls="" /></div>
          </nav>
        )}
      </header>
    </>
  );
};
